import React, { ReactNode, useMemo } from 'react';
import {
  ApolloLink,
  ApolloClient,
  ApolloProvider as BaseApolloProvider,
  InMemoryCache,
  NormalizedCacheObject,
  createHttpLink,
  ServerError
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useMsal, IMsalContext } from '@azure/msal-react';
import typePolicies from 'graph/type-policies';
import GET_ME from 'graph/user/get-me-query';
import GET_UI from 'store/apollo-cache-queries/getUiQuery';
import initApolloStore from 'store/init-apollo-store';
import {
  extractAuthHeadersFromCookies,
  hasAuthCookies,
  removeAuthCookies
} from 'utilities/auth-cookie-util';
import Config from '../config';
import { getLoginRequest } from '../config/auth';

const setupClient = (azureContext: IMsalContext): ApolloClient<NormalizedCacheObject> => {
  const { instance, inProgress } = azureContext;

  const getToken = async (): Promise<string | null> => {
    const accounts = await instance.getAllAccounts();

    const account = accounts?.[0];
    const loginRequest = getLoginRequest(account);

    if (account && inProgress === InteractionStatus.None) {
      try {
        const result = await instance.acquireTokenSilent(loginRequest);
        return result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.loginRedirect(loginRequest);
        } else {
          throw error;
        }
      }
    }

    return null;
  };

  const authLink = setContext(async (_, { headers: existingHeaders }) => {
    let authHeaders;
    if (hasAuthCookies()) {
      authHeaders = extractAuthHeadersFromCookies();
    } else {
      const token = await getToken();
      authHeaders = { ...(token && { Authorization: `Bearer ${token}` }) };
    }

    const headers = { ...existingHeaders, ...authHeaders };
    return { headers };
  });

  const link = createHttpLink({ uri: Config.graphUrl });

  const errorLink = onError(({ networkError }) => {
    if ((networkError as ServerError)?.statusCode === 401) {
      removeAuthCookies();
      instance.logoutRedirect();
    }
  });

  const retryLink = new RetryLink({
    attempts: {
      max: 3,
      // Only retry report-related Gateway Timeouts for now.
      retryIf: (error, operation): boolean => {
        const isReporting =
          operation.operationName.endsWith('Report') ||
          operation.operationName.endsWith('ReportExporting');

        return !!error && error.statusCode === 504 && isReporting;
      }
    }
  });

  const cache = new InMemoryCache({ typePolicies });
  const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    cache,
    link: ApolloLink.from([authLink, retryLink, errorLink, link])
  });

  const store = initApolloStore();
  client.writeQuery({ query: GET_UI, data: { ui: store.ui } });
  client.writeQuery({ query: GET_ME, data: { me: store.me } });

  return client;
};

const ApolloProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const azureContext = useMsal();
  const client = useMemo(() => setupClient(azureContext), [azureContext]);

  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
};

export default ApolloProvider;
